/**
 * WB Auto Gift
 * - Trigger products: configured via collection (variant IDs in window.__wbAutoGift)
 * - Sample at threshold, pouch at higher threshold
 * - Listens to document "cart:update" (Horizon shared event)
 */
(function () {
  'use strict';

  if (window.__wbAutoGiftBooted) return;
  window.__wbAutoGiftBooted = true;

  const SOURCE = 'wb-auto-gift';
  const GIFT_PROP = '_wb_gift';
  const SAMPLE_ROLE = 'sample';
  const POUCH_ROLE = 'pouch';

  let inFlight = false;
  let queued = false;

  function getConfig() {
    return window.__wbAutoGift || null;
  }

  function routes() {
    const themeRoutes = (window.Theme && window.Theme.routes) || {};
    return {
      cart: themeRoutes.cart_url || '/cart.js',
      add: themeRoutes.cart_add_url || '/cart/add.js',
      change: themeRoutes.cart_change_url || '/cart/change.js',
    };
  }

  function cartUrl() {
    const url = routes().cart;
    return url.endsWith('.js') ? url : url.replace(/\/?$/, '') + '.js';
  }

  function collectSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  }

  function dispatchCartUpdate(cart, sections) {
    const detail = {
      resource: cart || {},
      sourceId: SOURCE,
      data: {
        source: SOURCE,
        itemCount: cart && typeof cart.item_count === 'number' ? cart.item_count : undefined,
        sections: sections || {},
      },
    };
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: detail,
      })
    );
  }

  function isGiftEvent(event) {
    const source = event && event.detail && event.detail.data && event.detail.data.source;
    return source === SOURCE;
  }

  function fetchCart() {
    return fetch(cartUrl(), { credentials: 'same-origin' }).then((res) => {
      if (!res.ok) throw new Error('cart fetch failed');
      return res.json();
    });
  }

  function getLineGiftRole(item) {
    const props = item.properties || {};
    if (props[GIFT_PROP] === SAMPLE_ROLE || props[GIFT_PROP] === POUCH_ROLE) {
      return props[GIFT_PROP];
    }
    return null;
  }

  function sumTriggerQuantity(cart, config) {
    const triggerIds = new Set((config.triggerVariantIds || []).map(String));
    let total = 0;

    (cart.items || []).forEach((item) => {
      if (getLineGiftRole(item)) return;
      if (triggerIds.has(String(item.variant_id))) {
        total += item.quantity || 0;
      }
    });

    return total;
  }

  function findGiftLines(cart, role) {
    return (cart.items || []).filter((item) => getLineGiftRole(item) === role);
  }

  function findGiftLinesByVariantIds(cart, variantIds) {
    const ids = new Set((variantIds || []).map(String));
    return (cart.items || []).filter((item) => ids.has(String(item.variant_id)));
  }

  function pickRandomSample(config, excludeVariantIds) {
    const excluded = new Set((excludeVariantIds || []).map(String));
    const candidates = (config.samples || []).filter((sample) => {
      if (!sample || !sample.variantId) return false;
      if (excluded.has(String(sample.variantId))) return false;
      return sample.available !== false;
    });

    if (!candidates.length) return null;
    return candidates[Math.floor(Math.random() * candidates.length)];
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const error = new Error((data && (data.description || data.message)) || 'cart api failed');
        error.status = res.status;
        error.payload = data;
        throw error;
      }
      return data;
    });
  }

  function addGiftVariant(variantId, role, sectionIds) {
    const payload = {
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: {},
        },
      ],
    };
    payload.items[0].properties[GIFT_PROP] = role;

    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    return postJson(routes().add, payload);
  }

  function removeLine(lineKey, sectionIds) {
    const payload = {
      id: lineKey,
      quantity: 0,
    };

    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    return postJson(routes().change, payload);
  }

  async function removeLines(lines, sectionIds) {
    let lastResponse = null;
    for (const line of lines) {
      lastResponse = await removeLine(line.key, sectionIds);
    }
    return lastResponse;
  }

  async function addSampleWithRetry(config, sectionIds) {
    const tried = [];
    let lastError = null;

    while (tried.length < (config.samples || []).length) {
      const sample = pickRandomSample(config, tried);
      if (!sample) break;

      tried.push(String(sample.variantId));

      try {
        return await addGiftVariant(sample.variantId, SAMPLE_ROLE, sectionIds);
      } catch (error) {
        lastError = error;
        const sampleRef = (config.samples || []).find((s) => String(s.variantId) === String(sample.variantId));
        if (sampleRef) sampleRef.available = false;
      }
    }

    if (lastError) throw lastError;
    return null;
  }

  function mergeSections(responses) {
    const sections = {};
    (responses || []).forEach((response) => {
      if (response && response.sections) {
        Object.assign(sections, response.sections);
      }
    });
    return sections;
  }

  async function syncGiftState() {
    const config = getConfig();
    if (!config || !config.enabled) return;
    if (!(config.triggerVariantIds || []).length) return;

    const sectionIds = collectSectionIds();
    const cart = await fetchCart();
    const triggerQty = sumTriggerQuantity(cart, config);
    const wantSample = triggerQty >= (config.sampleThreshold || 2);
    const wantPouch = triggerQty >= (config.pouchThreshold || 3);

    let sampleLines = findGiftLines(cart, SAMPLE_ROLE);
    if (!sampleLines.length) {
      sampleLines = findGiftLinesByVariantIds(
        cart,
        (config.samples || []).map((s) => s.variantId)
      );
    }

    let pouchLines = findGiftLines(cart, POUCH_ROLE);
    if (!pouchLines.length && config.pouchVariantId) {
      pouchLines = findGiftLinesByVariantIds(cart, [config.pouchVariantId]);
    }

    const responses = [];

    if (!wantSample && sampleLines.length) {
      responses.push(await removeLines(sampleLines, sectionIds));
      sampleLines = [];
    }

    if (!wantPouch && pouchLines.length) {
      responses.push(await removeLines(pouchLines, sectionIds));
      pouchLines = [];
    }

    if (wantSample && !sampleLines.length) {
      const added = await addSampleWithRetry(config, sectionIds);
      if (added) responses.push(added);
    }

    if (wantPouch && !pouchLines.length && config.pouchVariantId && config.pouchAvailable !== false) {
      try {
        responses.push(await addGiftVariant(config.pouchVariantId, POUCH_ROLE, sectionIds));
      } catch (error) {
        config.pouchAvailable = false;
        console.warn('[wb-auto-gift] pouch add failed', error);
      }
    }

    if (!responses.length) return;

    const sections = mergeSections(responses);
    const latestCart = await fetchCart();
    dispatchCartUpdate(latestCart, sections);
  }

  function scheduleSync() {
    if (inFlight) {
      queued = true;
      return;
    }

    inFlight = true;
    syncGiftState()
      .catch((error) => {
        console.warn('[wb-auto-gift]', error);
      })
      .finally(() => {
        inFlight = false;
        if (queued) {
          queued = false;
          scheduleSync();
        }
      });
  }

  function onCartUpdate(event) {
    if (isGiftEvent(event)) return;
    scheduleSync();
  }

  function boot() {
    document.addEventListener('cart:update', onCartUpdate);
    scheduleSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
